import React, { MutableRefObject, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import { ReplacementGroup } from "../../replacement-group";
import Styles from "./Variables.module.scss";
import { FormField, FormFieldReplacementInput } from "../../../__generated__/api";
import VariablesToggle from "./VariablesToggle";

interface DisplayVariable {
    fieldId: string;
    groupId: string;
    replacement: string;
    text: string;
}

export interface VariablesInput {
    valueFormat?: "auto" | "fieldId"
    shouldReplaceVariables?: boolean;
    variables: ReplacementGroup[];
    selectedVariables?: FormFieldReplacementInput[];
}

function normalizeInput(input: string): string {
    return input.toLowerCase().replace(/\s/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
}

const Inline = Quill.import("blots/inline");

class VariableBlot extends Inline {
    static create(data: string) {
        const values = (typeof data === "string" ? JSON.parse(data) : data) as DisplayVariable;
        const div = super.create() as HTMLDivElement;
        div.dataset.fieldId = values.fieldId as string;
        div.dataset.groupId = values.groupId as string;
        div.dataset.replacement = values.replacement as string;
        div.dataset.text = values.text as string;
        div.contentEditable = "false";
        div.classList.add(Styles["variable-badge"]);
        // div.textContent = ` ${values.replacement} `;
        div.title = values.text;
        return div;
    }

    static formats(node: HTMLElement) {
        return JSON.stringify({
            fieldId: node.dataset.fieldId,
            groupId: node.dataset.groupId,
            replacement: node.textContent?.trim(),
            text: node.dataset.text,
        });
    }
}

export function insertVariables(html: string, variables: VariablesInput) {
    const doc = new DOMParser().parseFromString(html, "text/html");

    const variableMap = variables.selectedVariables?.toIdMap(row => row.replacement, row => {
        const group = variables.variables.find(current => current.id === row.groupId);
        const field = group?.input?.find(input => input.id === row.fieldId);
        return group && field ? {
            group,
            field,
        } : undefined;
    }) ?? {};
    variables.variables.forEach(group => {
        group.input?.forEach(input => {
            const replacement = variables.valueFormat === "auto"
                ? `{{${normalizeInput(group.label)}/${normalizeInput(input.name)}}}`
                : `{{${input.id}}}`;
            if (!variableMap[replacement]) {
                variableMap[replacement] = {
                    group,
                    field: input,
                };
            }
        });
    });

    const walker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT);
    let curr = walker.nextNode();
    while (curr) {
        if (curr.nodeType === Node.TEXT_NODE
            && curr.textContent
            && curr.parentElement?.tagName !== "VARIABLE") {
            const fragment = document.createDocumentFragment();
            const content = curr.textContent;
            let lastStart = 0;
            do {
                const start = content.indexOf("{{", lastStart);
                let nextStart = start < 0 ? content.length : start;
                fragment.appendChild(document.createTextNode(content.substring(lastStart, nextStart)));
                if (start >= 0) {
                    const replacementEnd = content.indexOf("}}", lastStart);
                    if (replacementEnd < 0) {
                        nextStart = content.length;
                    } else {
                        const replacement = content.substring(start, replacementEnd + 2);
                        nextStart += replacement.length;

                        const variable = variableMap[replacement];
                        if (variable) {
                            const blot = VariableBlot.create(JSON.stringify({
                                fieldId: variable.field.id,
                                groupId: variable.group.id,
                                replacement,
                                text: `${variable.field.name} (${variable.group.label})`,
                            }));
                            blot.textContent = replacement;
                            fragment.appendChild(blot);
                        } else {
                            fragment.appendChild(document.createTextNode(replacement));
                        }
                    }
                }

                lastStart = Math.max(nextStart, lastStart + 1);
            } while (lastStart < content.length);
            curr.parentNode?.replaceChild(fragment, curr);
        }
        curr = walker.nextNode();
    }

    return doc.querySelector("body")?.innerHTML as string;
}

VariableBlot.blotName = "variable";
VariableBlot.tagName = "variable";

Quill.register("blots/variable", VariableBlot);

export default function Variables({
    variables,
    quillRef,
}: {
    variables: VariablesInput;
    quillRef: MutableRefObject<ReactQuill | null>;
}) {
    const [isShowing, setIsShowing] = useState(false);

    const onClick = (group: ReplacementGroup, item: FormField) => {
        const editor = quillRef.current?.editor;
        if (!editor) {
            return;
        }
        const position = editor.getSelection(true) ?? { index: editor.getLength(), length: 0 };

        if (!position) {
            return;
        }

        const replacement = variables.valueFormat === "auto"
            ? `{{${normalizeInput(group.label)}/${normalizeInput(item.name)}}}`
            : `{{${item.id}}}`;

        const text = `${item.name} (${group.label})`;
        editor.deleteText(position.index, position.length, "silent");
        editor.insertText(position.index, replacement, "variable", {
            fieldId: item.id,
            groupId: group.id,
            replacement,
            text,
        }, "silent");

        setTimeout(() => editor.setSelection(position.index + position.length + text.length, 0, "silent"));
        setIsShowing(false);
    };

    return (
        <VariablesToggle
            onClick={onClick}
            isShowing={isShowing}
            setIsShowing={setIsShowing}
            replacements={variables.variables}
        />
    );
}
